import { derived, writable, type Readable, get } from 'svelte/store';
import {
	defaultEvmStores,
	selectedAccount,
	web3,
} from '../../stores/evmStores';
import { createDialogStores } from '$global/components/Dialog';
import { safePersistedStore } from '$global/utils/safePersistedStore/safePersistedStore';

export type Web3Context = ReturnType<typeof createWeb3Context>;

type ManualAddress = {
	address: string | null;
	prioritary: boolean;
	refresh: number;
};

export const createWeb3Context = (
	supportsManualAddress: boolean,
	intro: string,
	backButtonProps?: { href: string; text: string }
) => {
	const ready = writable(false);
	const shouldCloseConnectDialog = writable(true);
	const manualAddress = safePersistedStore('heni-manual-wallet-address', {
		address: null,
		prioritary: false,
		refresh: 0,
	} as ManualAddress);

	const enableWeb3Address = () => ready.set(true);
	const connectMetamask = async () => {
		await defaultEvmStores.setProvider();
		manualAddress.update(($manualAddress) => ({
			address: $manualAddress?.address,
			prioritary: false,
			refresh: ($manualAddress?.refresh || 0) + 1,
		}));
		return Promise.resolve(true);
	};

	const disconnect = () => defaultEvmStores.disconnect();
	const account = derived([manualAddress, web3], (async (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		[$manualAddress, __]: [ManualAddress, any],
		set: (value: string | null | undefined) => void
	) => {
		if (typeof window !== 'undefined') {
			const accounts = await get(web3)?.eth?.getAccounts?.();
			const selectedAccount = accounts?.[0] as string | undefined;

			if (!supportsManualAddress) {
				set(selectedAccount);
				return;
			}

			if ($manualAddress?.prioritary) {
				set($manualAddress?.address);
			} else {
				set(selectedAccount);
			}
		}
	}) as Parameters<typeof derived>[1]) as Readable<string | null | undefined>;

	const lowerCasedAccount = derived(
		[selectedAccount, manualAddress],
		([$selectedAccount, $manualAddress]) => {
			if (!supportsManualAddress) {
				return $selectedAccount;
			}

			if ($manualAddress?.prioritary) {
				return $manualAddress?.address;
			} else {
				return $selectedAccount;
			}
		}
	);

	const setManualAddress = (address: ManualAddress) =>
		manualAddress.set(address);

	const handleClickConnectButton = async (
		onSubmit?: undefined | (() => void)
	) => {
		try {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			if (!(window as any).ethereum) {
				console.error('MetaMask is not installed');
				return;
			}

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			await (window as any).ethereum.request({
				method: 'wallet_requestPermissions',
				params: [
					{
						eth_accounts: {},
					},
				],
			});

			await connectMetamask();
			if (onSubmit) {
				onSubmit();
			}
		} catch (error) {
			console.error('Failed to connect to MetaMask:', error);
		}
	};

	const connectDialogStores = createDialogStores();
	const {
		states: { open },
	} = connectDialogStores;

	const openConnectDialog = (shouldClose: boolean) => {
		shouldCloseConnectDialog.set(shouldClose);
		open.set(true);
	};

	const closeConnectDialog = () => {
		open.set(false);
	};

	return {
		ready,
		account,
		lowerCasedAccount,
		supportsManualAddress,
		manualAddress,
		setManualAddress,
		enableWeb3Address,
		connectMetamask,
		disconnect,
		handleClickConnectButton,
		connectDialogStores,
		openConnectDialog,
		closeConnectDialog,
		shouldCloseConnectDialog,
		backButtonProps,
		intro,
		...(supportsManualAddress && {
			setManualAddress,
		}),
	};
};
